import React, { useEffect, useMemo, useState } from 'react';
import { PreguntaPortada, Dificultad, Modalidad, CheckAnswerResponse } from '../types';
import { getImageUrl } from '../config';
import Autocomplete from './Autocomplete';

interface Props {
  pregunta: PreguntaPortada;
  onResponder: (respuestaPrincipal: string, respuestaSecundaria?: string) => void;
  respondida: boolean;
  fueCorrecta: boolean | null;
  opciones: string[];
  dificultad: Dificultad;
  desglose?: CheckAnswerResponse['desglose'];
  onContinuar?: () => void;
  esUltima?: boolean;
  esUltimaDelNivel?: boolean;
  modalidad?: Modalidad;
  puntosGanados?: number;
}

const Level2Portada: React.FC<Props> = ({ pregunta, onResponder, respondida, fueCorrecta, opciones, dificultad, desglose, onContinuar, esUltima, esUltimaDelNivel, modalidad, puntosGanados = 0 }) => {
  const normalizeOption = (text: string) => text
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

  const [titulo, setTitulo] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [displayImageUrl, setDisplayImageUrl] = useState(getImageUrl(pregunta.portada));
  const letras = ['A', 'B', 'C', 'D'];

  const mezclarOpciones = (arr: string[]) => {
    const copia = [...arr];
    for (let i = copia.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [copia[i], copia[j]] = [copia[j], copia[i]];
    }
    return copia;
  };

  const opcionesNormal = useMemo(() => {
    if (dificultad !== 'normal') return [];
    const correcta = pregunta.respuesta;
    const incorrectas = opciones.filter(o => o !== correcta);
    const mezcladas = mezclarOpciones(incorrectas).slice(0, 3);
    return mezclarOpciones([...mezcladas, correcta]);
  }, [dificultad, opciones, pregunta.id, pregunta.respuesta]);

  useEffect(() => {
    setTitulo('');
    const siguienteImagen = getImageUrl(pregunta.portada);

    // Mantiene la imagen actual visible y solo hace swap cuando la siguiente ya esta lista.
    const preload = new Image();
    preload.src = siguienteImagen;
    preload.onload = () => {
      setDisplayImageUrl(siguienteImagen);
      setImageLoaded(true);
    };
    preload.onerror = () => {
      setDisplayImageUrl(siguienteImagen);
      setImageLoaded(true);
    };
  }, [pregunta.id, pregunta.portada]);

  const handleConfirmar = () => {
    if (puedeConfirmar) {
      if (dificultad === 'dificil') {
        onResponder('', titulo.trim());
      } else {
        onResponder(titulo.trim());
      }
    }
  };

  const puedeConfirmar = dificultad === 'dificil'
    ? opciones.some(o => normalizeOption(o) === normalizeOption(titulo))
    : titulo.trim().length > 0;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.repeat) return;

      if (respondida) {
        if (onContinuar) {
          e.preventDefault();
          onContinuar();
        }
        return;
      }

      if (puedeConfirmar) {
        e.preventDefault();
        handleConfirmar();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [respondida, onContinuar, puedeConfirmar, titulo, dificultad, onResponder]);

  const getEstiloOpcion = (opcion: string) => {
    const isSelected = titulo === opcion;
    const isCorrect = opcion === pregunta.respuesta;

    if (respondida) {
      if (isSelected && fueCorrecta) {
        return {
          wrapper: 'border-green-500 bg-green-100 shadow-lg scale-[1.01]',
          text: 'opcion-texto-correcto',
          circle: 'border-green-500 bg-green-500 text-white',
        };
      }
      if (isCorrect) {
        return {
          wrapper: 'border-green-500 bg-green-100 shadow-lg scale-[1.01]',
          text: 'opcion-texto-correcto',
          circle: 'border-green-500 bg-green-500 text-white',
        };
      }
      if (isSelected && !fueCorrecta) {
        return {
          wrapper: 'border-red-500 bg-red-100 shadow-lg scale-[1.01]',
          text: 'opcion-texto-incorrecto',
          circle: 'border-red-500 bg-red-500 text-white',
        };
      }
      return {
        wrapper: 'border-gray-400 bg-gray-100',
        text: 'opcion-texto-resuelto',
        circle: 'border-gray-400 bg-gray-400 text-white',
      };
    }

    if (isSelected) {
      return {
        wrapper: 'border-yellow-500 bg-yellow-100 shadow-lg scale-[1.01]',
        text: 'opcion-texto-seleccionado',
        circle: 'border-yellow-500 bg-yellow-500 text-white',
      };
    }

    return {
      wrapper: 'border-slate-300 bg-white hover:border-emerald-300',
      text: 'opcion-texto-normal',
      circle: 'border-slate-400 text-slate-500 bg-white',
    };
  };

  const getTituloFieldColor = () => {
    if (!respondida) {
      return 'bg-white border-2 border-emerald-200 text-slate-700';
    }

    if (dificultad === 'dificil' && desglose) {
      const sagaOk = !!desglose.sagaCorrect;
      const tituloOk = !!desglose.tituloCorrect;

      if (sagaOk && tituloOk) {
        return 'bg-green-100 border-2 border-green-500 text-green-800';
      }
      if (sagaOk && !tituloOk) {
        return 'bg-amber-100 border-2 border-amber-400 text-amber-900';
      }
      return 'bg-red-200 border-2 border-red-700 text-red-900';
    }

    return fueCorrecta
      ? 'bg-green-100 border-2 border-green-500 text-green-800'
      : 'bg-red-200 border-2 border-red-700 text-red-900';
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-center mb-6">
        <div className="max-w-4xl w-full">
          <div className={`game-image-container transition-all duration-500 ${
            respondida
              ? fueCorrecta ? 'border-green-500' : 'border-red-500'
              : 'border-emerald-300'
          }`}>
            <img
              src={displayImageUrl}
              alt="Fondo borroso"
              className={`game-image-background-fill ${imageLoaded ? 'loaded' : 'loading'}`}
              onLoad={() => setImageLoaded(true)}
            />

          <img
            src={displayImageUrl}
            alt="Portada del juego"
            className={`game-image-super-simple ${imageLoaded ? 'loaded' : 'loading'} ${respondida ? 'resolved' : 'blurred'}`}
            onLoad={() => setImageLoaded(true)}
          />
          {respondida && !fueCorrecta && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <p className="font-main font-bold text-white text-xl text-center px-4">{pregunta.respuesta}</p>
            </div>
          )}
        </div>
        </div>
      </div>

      {dificultad === 'normal' ? (
        <div className="w-full max-w-[500px] mx-auto space-y-3 bg-transparent p-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {opcionesNormal.map((opcion, i) => {
              const estilo = getEstiloOpcion(opcion);
              return (
                <button
                  key={`${pregunta.id}-${i}-${opcion}`}
                  type="button"
                  onClick={() => !respondida && setTitulo(opcion)}
                  disabled={respondida}
                  className={`opcion-enhanced p-4 rounded-lg border-2 transition-all duration-200 min-h-[84px] flex items-center w-full text-left ${estilo.wrapper}`}
                >
                  <div className="flex items-center w-full">
                    <span className={`circulo-letra-enhanced w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0 ${estilo.circle}`}>
                      {letras[i]}
                    </span>
                    <span className={`text-base md:text-lg flex-1 leading-tight ${estilo.text}`}>
                      {opcion}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {!respondida && (
            <button
              onClick={handleConfirmar}
              disabled={!puedeConfirmar}
              className="btn-primary mt-2 w-full font-gaming text-xs tracking-wider py-4 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              CONFIRMAR RESPUESTA
            </button>
          )}

          {respondida && onContinuar && (
            <button
              onClick={onContinuar}
              className="btn-primary w-full font-gaming text-xs tracking-wider py-4"
            >
              {puntosGanados === 0 && modalidad === 'nohit'
                ? 'VER RESULTADOS'
                : esUltima
                  ? 'VER RESULTADOS'
                  : esUltimaDelNivel
                    ? 'SIGUIENTE NIVEL →'
                    : 'SIGUIENTE PORTADA →'
              }
            </button>
          )}
        </div>
      ) : !respondida ? (
        <div className="w-full max-w-[500px] mx-auto space-y-3 panel-respuesta-enhanced p-4">
          <div>
            <label className="font-main text-[#d9fff2] text-sm font-semibold mb-2 block uppercase tracking-wider drop-shadow-[0_0_6px_rgba(0,255,170,0.35)]">Título exacto del juego</label>
            <Autocomplete
              opciones={opciones}
              valor={titulo}
              onChange={setTitulo}
              placeholder="Ej: Ocarina of Time..."
            />
          </div>
          <button
            onClick={handleConfirmar}
            disabled={!puedeConfirmar}
            className="btn-primary mt-2 w-full font-gaming text-xs tracking-wider py-4 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            CONFIRMAR RESPUESTA
          </button>
        </div>
      ) : (
        <div className="w-full max-w-[500px] mx-auto space-y-4 text-center">
          <div className={`p-4 rounded-xl font-main text-lg font-bold ${getTituloFieldColor()}`}>
            {titulo || '(vacío)'}
          </div>

          {onContinuar && (
            <button onClick={onContinuar} className="btn-primary w-full font-gaming text-xs tracking-wider py-4">
              {puntosGanados === 0 && modalidad === 'nohit'
                ? 'VER RESULTADOS'
                : esUltima
                  ? 'VER RESULTADOS'
                  : esUltimaDelNivel
                    ? 'SIGUIENTE NIVEL →'
                    : 'SIGUIENTE PORTADA →'
              }
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Level2Portada;
